// As regras puras da tela de plano: como o status vira o que se mostra.
import type { Conversas, EstadoDaAssinatura, PlanoOferecido, StatusDoPlano } from './tipos';

export const centavos = (c: number | null | undefined) => 'R$ ' + ((c || 0) / 100).toFixed(2).replace('.', ',');
export const reais = (v: number | string | null | undefined) => 'R$ ' + (Number(v) || 0).toFixed(2).replace('.', ',');
export const reaisInteiros = (c: number) => 'R$ ' + (c / 100).toLocaleString('pt-BR', { minimumFractionDigits: 0 });
export const porcento = (v: number | string | null | undefined) => String(v ?? '').replace('.', ',') + '%';
export const dia = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');

export const ROTULO_DA_SITUACAO: Record<string, string> = {
  trial_pending: 'aguardando pagamento',
  trial_active: 'trial · 7 dias',
  active: 'ativo',
  trial_expired: 'trial expirado',
  expired: 'expirado',
  cancelled: 'cancelado',
};
export const rotuloDaSituacao = (status: string) => ROTULO_DA_SITUACAO[status] || 'ativo';

const TRIAL = ['trial_pending', 'trial_active'];

/**
 * O plano que o card mostra. Durante o trial é o ESCOLHIDO (Solo/Loja), não
 * "trial"; o preço é o snapshot do cliente (a régua dele), e só na falta dele
 * o da prateleira.
 */
export function planoExibido(s: StatusDoPlano): { code: string; name: string; monthlyCents: number; trial: boolean } {
  const sub = s.subscription;
  const trial = TRIAL.includes(sub.status);
  return {
    code: trial ? (sub.chosen_plan_code || 'trial') : s.plan.code,
    name: trial ? (sub.chosen_plan_name || 'Trial') : s.plan.name,
    monthlyCents: sub.chosen_monthly_cents || s.plan.price_cents,
    trial,
  };
}

/** Sem assinatura valendo (grátis, trial pendente, vencida): o caminho é assinar, não fazer upgrade. */
export function precisaAssinar(s: StatusDoPlano): boolean {
  return s.plano_efetivo.code === 'free' || ['trial_pending', 'trial_expired', 'expired', 'cancelled'].includes(s.subscription.status);
}

export function resumoDoPlano(p: PlanoOferecido): string {
  return [
    `${p.monthly_conversations.toLocaleString('pt-BR')} conversas/mês`,
    `${porcento(p.marketplace_fee_pct)} por venda`,
    p.max_numbers === 1 ? '1 número' : `até ${p.max_numbers} números`,
    p.max_products == null ? 'catálogo ilimitado' : `${p.max_products} produtos`,
  ].join(' · ');
}

/** Os planos à venda que não são o atual (nem o escolhido pro fim do trial), do mais barato pro mais caro. */
export function opcoesDeTroca(s: StatusDoPlano): PlanoOferecido[] {
  const atual = s.subscription.chosen_plan_code || planoExibido(s).code;
  return (s.plans_offered || []).filter((p) => p.code !== atual).sort((a, b) => a.price_cents - b.price_cents);
}

export type NivelDaBarra = '' | 'aviso' | 'cheia';
export function nivelDaBarra(pct: number | null): NivelDaBarra {
  if (pct == null) return '';
  if (pct >= 100) return 'cheia';
  if (pct >= 80) return 'aviso';
  return '';
}

export type Alerta =
  | { tipo: 'gratis' }
  | { tipo: 'decidir'; used: number; limit: number; overageCents: number; extra: string }
  | { tipo: 'autorizado'; overageCents: number; extra: string }
  | { tipo: 'aviso'; pct: number; faltam: number; overageCents: number }
  | null;

/** A faixa acima das barras: em 80% avisa; em 100% pergunta; verde quando o excedente já foi autorizado. */
export function alertaDeConversas(c: Conversas | null): Alerta {
  if (!c || c.limit == null) return null;
  // Plano grátis: o limite é zero porque a Bia não vem no plano, não porque a cota acabou.
  if (c.gratuito) return { tipo: 'gratis' };
  const extra = c.overage_used ? ` Já são ${c.overage_used} conversa(s) extra este mês, a ${centavos(c.overage_cents)} cada.` : '';
  if (c.needs_decision || c.blocked) return { tipo: 'decidir', used: c.used, limit: c.limit, overageCents: c.overage_cents, extra };
  if (c.decision === 'continue') return { tipo: 'autorizado', overageCents: c.overage_cents, extra };
  if (c.pct >= 80) return { tipo: 'aviso', pct: c.pct, faltam: Math.max(0, c.limit - c.used), overageCents: c.overage_cents };
  return null;
}

/** As formas de pagar o anual: à vista, ou parcelado até o teto. O total é o mesmo em qualquer uma. */
export function opcoesDeParcelas(maxParcelas: number, total: number): { n: number; rotulo: string }[] {
  const brl = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  return [1, 3, 6, 12].filter((n) => n <= (maxParcelas || 1)).map((n) => ({
    n,
    rotulo: n === 1 ? `À vista — R$ ${brl(total)} (Pix, boleto ou cartão)` : `${n}x de R$ ${brl(total / n)} no cartão, sem juros`,
  }));
}

export const ESTADO_DA_VENDA: Record<string, { cor: string; ajuda: string }> = {
  'aguardando pagamento': { cor: 'apoio', ajuda: 'ainda não pago' },
  'em consolidação': { cor: 'aviso', ajuda: 'no prazo de arrependimento' },
  'consolidada': { cor: 'acento', ajuda: 'entra na próxima fatura' },
  'cancelada': { cor: 'apoio', ajuda: 'não gera comissão' },
  'creditada': { cor: 'info', ajuda: 'volta como crédito' },
};

/** Já usou o teste (ou a assinatura venceu): cobra o valor cheio, sem oferecer outro R$ 5. */
export const usouOTrial = (s: EstadoDaAssinatura) => !!s.trial_used || ['trial_expired', 'expired', 'cancelled'].includes(s.status);
export function textosDoPaywall(s: EstadoDaAssinatura) {
  const usou = usouOTrial(s);
  const preco = s.trial_price ?? 5, dias = s.trial_days ?? 7;
  return usou
    ? { titulo: 'Escolha seu plano. Sem fidelidade, cancele quando quiser.', subtitulo: 'Seu período de teste terminou. Assine pra continuar com o vendedor de IA respondendo seus clientes.', ajuda: 'A 1ª mensalidade é cobrada hoje e depois renova todo mês. Você pode cancelar a qualquer momento em Plano e cobrança.', detalheDoPlano: 'Cobrança mensal', botao: '🔒 Assinar agora' }
    : { titulo: `${dias} dias por R$ ${preco}. Sem fidelidade, cancele quando quiser.`, subtitulo: `Teste o vendedor de IA sem compromisso. Se em qualquer momento dos ${dias} dias você não gostar, basta cancelar em 1 clique e não pagamos a mensalidade.`, ajuda: `Após os ${dias} dias, será cobrado o valor mensal do plano escolhido. Você pode cancelar a qualquer momento em Plano e cobrança.`, detalheDoPlano: `Após o trial de ${dias} dias`, botao: `🔒 Pagar R$ ${preco} e começar agora` };
}
/**
 * A tarja do topo: trial acabando (2 dias ou menos) ou assinatura vencida.
 *
 * "Vencida" vem do PLANO EFETIVO (`/plan/status` → `plano_efetivo.rebaixado`),
 * e não do status cru da assinatura: é o plano efetivo que conhece o
 * `PAYWALL_EXEMPT` (por loja E por e-mail) e os founders — o painel antigo
 * decidia por ele. Pelo status cru, uma conta isenta com a assinatura vencida
 * via a tarja de "reativar" (achado pelo Bruno na produção, 2026-09-25).
 */
export function tarjaDaAssinatura(s: EstadoDaAssinatura, efetivo: { rebaixado: boolean }): { texto: string; destino: string; tom: 'aviso' | 'perigo' } | null {
  if (efetivo.rebaixado) return { texto: '⚠️ Sua assinatura venceu e a loja voltou pro plano grátis — a Bia parou de responder. Clique pra reativar.', destino: '/app/assinar', tom: 'perigo' };
  if (s.status === 'trial_active' && s.trial_days_left != null && s.trial_days_left <= 2) return { texto: `⏰ Seu trial expira em ${s.trial_days_left} dia${s.trial_days_left === 1 ? '' : 's'}. Clique pra garantir sua assinatura e não perder o acesso.`, destino: '/app/plano', tom: 'aviso' };
  return null;
}
